// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template v-if="isLoading"></template>
<template v-else>
  <detail-view
  v-model="item"
  :endpoint="endpoint + 'things'"
  name="Thing"
  permissions="things"
  :values="values"
  ></detail-view>
</template>
<script>
import {ENDPOINT} from '@/shared/constants'
import DetailView from '@sdgsystems/vue-common/components/DetailView.vue'
import RelationshipList from '@/components/RelationshipList.vue'
import {SettingsMixin, EventsMixin, TimeMixin} from '@/shared/mixins'
import { mapGetters } from 'vuex'
import Rules from '@sdgsystems/vue-common/components/rules'

export default {
  components: {
    DetailView,
    RelationshipList
  },

  mixins: [SettingsMixin, EventsMixin, TimeMixin],

  data () {
    return {
      isLoading: false,
      endpoint: ENDPOINT,
      item: null,
      itemFields: []
    }
  },

  created () {
    if (Object.keys(this.siteSettings).length > 0) {
      this.isLoading = false
    }

    console.log('Site Settings: ', this.siteSettings)
  },

  computed: {
    ...mapGetters([
      'selectedSite',
      'siteSettings'
    ]),

    values: function () {
      let customFields = []
      if (this.siteSettings.fieldConfig && this.siteSettings.fieldConfig.customFields) {
        customFields = this.siteSettings.fieldConfig.customFields.map(field => {
          const customValue = 'data.' + field.genericField.charAt(0).toLowerCase() + field.genericField.slice(1)
          if (field.genericType === 'number') {
            return {
              name: field.label,
              value: customValue,
              rule: Rules.validateInteger,
              valueType: 'number'
            }
          } else if (field.genericType === 'float') {
            return {
              name: field.label,
              value: customValue,
              rule: Rules.validateFloat,
              valueType: 'number'
            }
          } else {
            return {
              name: field.label,
              value: customValue
            }
          }
        })
      }
      return [
        {
          name: 'Basic',
          items: [
            [
              {
                name: this.getItemNameLabel(this.siteSettings.itemNameLabel),
                value: 'common.name'
              },
              {
                name: this.getItemSerialLabel(this.siteSettings.itemSerialLabel),
                value: 'common.serial'
              },
              {
                name: 'Class',
                value: 'common.class',
                context: ENDPOINT + 'thingclasses',
                type: 'select',
                multiple: false,
                required: true,
                disabled: !this.isNew
              },
              {
                name: 'Tag1',
                value: 'common.tag1'
              },
              {
                name: 'Tag2',
                value: 'common.tag2'
              },
              {
                name: 'Tags',
                value: 'common.tags',
                type: 'combobox',
                multiple: true,
                width: 'xs12'
              },
              {
                name: 'Categories',
                value: 'common.categories',
                context: ENDPOINT + 'image-categories',
                type: 'select',
                multiple: true,
                width: 'xs12',
                itemText: 'name',
                itemValue: 'id'
              },
              {
                name: 'Labels',
                value: 'common.labels',
                type: 'combobox',
                multiple: true
              },
              {
                name: 'Time Created',
                value: 'common.timeCreated',
                type: 'formatTime',
                disabled: true
              },
              {
                name: 'Time Modified',
                value: 'common.timeModified',
                type: 'formatTime',
                disabled: true
              },
              ...customFields
            ],
            [
              {
                name: 'Description',
                value: 'common.description',
                width: 'xs12'
              }
            ],
            [
              {
                name: 'Last Event',
                value: 'state.lastEvent',
                type: 'formatTime',
                disabled: true
              },
              {
                name: 'Last Endpoint Change',
                value: 'state.lastEndpointTime',
                type: 'formatTime',
                disabled: true
              },
              {
                name: 'Last Zone Transition',
                value: 'state.lastZoneTime',
                type: 'formatTime',
                disabled: true
              },
              {
                name: 'Zone',
                value: 'state.zone',
                context: ENDPOINT + 'zones',
                type: 'select',
                itemText: 'name',
                itemValue: '_id',
                disabled: true
              },
              {
                name: 'Endpoint',
                value: 'state.endpoint',
                context: ENDPOINT + 'eventsources',
                type: 'select',
                itemText: 'name',
                itemValue: '_id',
                disabled: true
              },
              {
                name: 'Location Tag',
                value: 'state.locationTag',
                context: ENDPOINT + 'locationtags',
                type: 'select',
                itemText: 'name',
                itemValue: '_id',
                disabled: true
              },
              {
                name: 'Coordinates',
                value: 'state.xycoordinates',
                disabled: true
              },
              {
                name: 'Map',
                value: 'state.map',
                context: ENDPOINT + 'maps',
                type: 'select',
                itemText: 'name',
                itemValue: '_id',
                disabled: true
              }
            ],
            [
              {
                name: 'Alarm',
                value: 'state.alarmTamper',
                disabled: true
              },
              {
                name: 'Alarm Tag',
                value: 'state.alarmTamperTag',
                disabled: true
              },
              {
                name: 'Alarm Time',
                value: 'state.alarmTamperTime',
                type: 'formatTime',
                disabled: true
              }
            ]
          ]
        },
        {
          name: 'Events',
          items: [
            [
              {
                name: 'Events',
                value: ENDPOINT + 'events',
                pagination: true,
                type: 'itemList',
                id: '_id',
                filter: [{
                  name: 'thing',
                  value: '_id'
                }],
                headers: [
                  {
                    text: 'Tag',
                    value: 'tag',
                    function: this.getTagName,
                    sortable: true
                  },
                  {
                    text: 'Last Full Event',
                    value: 'firstTime',
                    function: this.formatFirstTime,
                    sortable: false
                  },
                  {
                    text: 'Last Seen',
                    value: 'time',
                    function: this.formatTimeWrapper,
                    sortable: true
                  },
                  {
                    text: 'Repeat Event Count',
                    value: 'repeatCount',
                    function: this.formatRepeatCount,
                    sortable: false
                  },
                  {
                    text: 'Direction',
                    value: 'direction',
                    sortable: false
                  },
                  {
                    text: 'Data',
                    value: 'data.cFloat0',
                    sortable: true
                  },
                  {
                    text: 'Endpoint',
                    value: 'endpoint',
                    context: ENDPOINT + 'endpoints',
                    function: this.getEndpointName,
                    sortable: true
                  },
                  {
                    text: 'Antenna',
                    value: 'antenna',
                    sortable: true
                  },
                  {
                    text: 'Zone',
                    value: 'zone',
                    context: ENDPOINT + 'zones',
                    function: this.getZoneName,
                    sortable: true
                  }
                ]
              }
            ]
          ]
        },
        {
          name: 'Zone History',
          items: [
            [
              {
                text: 'Zone History',
                endpoint: ENDPOINT + 'zonedurations',
                type: 'expansionView'
              }
            ]
          ]
        },
        {
          name: 'Photo',
          items: [
            [
              {
                text: 'Photo',
                value: 'common',
                type: 'component',
                component: 'worker-photo'
              }
            ]
          ]
        },
        {
          name: 'Data',
          items: [
            [
              {
                name: 'Data',
                value: 'data.cFloat0',
                disabled: true
              }
            ]
          ]
        },
        {
          name: 'Parents/Children',
          items: [
            [
              {
                type: 'customType',
                value: RelationshipList,
                width: 'xs12',
                contextual: ['thingID'],
                props: {
                  label: 'Asset Linking',
                  thingID: '_id'
                }
              }
            ]
          ]
        }
      ]
    },

    itemClass: function () {
      var itemClass = ''
      if (this.$route.path.includes('items')) {
        itemClass = 'item'
      } else if (this.$route.path.includes('workers')) {
        itemClass = 'worker'
      }
      return itemClass
    },

    isNew: function () {
      return this.$route.params.id === 'new'
    }
  },

  watch: {
    item: function () {
      if (this.item) {
        if (!this.item.common && this.itemClass !== '') {
          this.item.common = {}
          this.item.common.class = this.itemClass
        }
      }
    },

    '$route.params.id': function () {
      console.log('route id changed')
      this.$router.go()
    },

    siteSettings: function () {
      this.isLoading = false
    }
  },

  methods: {
    formatTimeWrapper: function (item) {
      return this.formatTime(item.time)
    },

    getEndpointName: function (item, endpoints) {
      if (item.endpoint !== '' && endpoints) {
        var e = endpoints.find((el) => el._id === item.endpoint)
        if (e) {
          return e.name
        }
      }
      return 'N/A'
    },

    getTagName: function (item) {
      var name = item.tag
      if (item.alarmTamper) {
        name = name + ' - ' + '(Alarm)'
      }
      return name
    },

    getZoneName: function (item, zones) {
      if (item.zone !== '' && zones) {
        var e = zones.find((el) => el._id === item.zone)
        if (e) {
          return e.name
        }
      }
      return 'N/A'
    },

    formatFloat: function (value) {
      return parseFloat(value)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ThingEdit.vue